// Main Manager Class for the Main Section
// Handles business logic and state management for the landing page
// Follows the Manager and Component pattern adapted from AgentGroupChat
// Integrates with AppCreator AI stack (Tool + AI Tool + Inference; add BubbleUiSystem when that UI needs bubbles)

import { Manager } from "../../../core";
import { logger } from "../../../core";
import { navigationManager } from "../../../managers/navigation-manager";

// App Creator Systems
import { AppCreatorToolSystem } from "../../../systems/core/instances/appCreator/base/AppCreatorToolSystem.js";
import { AppCreatorAIToolSystem } from "../../../systems/core/instances/appCreator/base/AppCreatorAIToolSystem.js";
import { AppCreatorAIInferenceSystem } from "../../../systems/core/instances/appCreator/base/AppCreatorAIInferenceSystem.js";
import { AppCreatorToolSet } from "../../../systems/core/instances/appCreator/implementations/ai/AppCreatorToolSet.js";

export class MainManager extends Manager {
  constructor() {
    super();
    this.heroData = {
      title: "Welcome to Revolt",
      subtitle: "Build and deploy AI-powered applications with ease",
      ctaText: "Get Started",
    };
    this.isInitialized = false;

    // App Creator systems (three-class stack; not the full workspace quadrad)
    this.appCreatorToolSystem = null;
    this.appCreatorAIToolSystem = null;
    this.appCreatorAIInferenceSystem = null;
  }

  /**
   * Initialize global variables and setup
   * In React: Sets up navigation manager connection
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
    // The navigate function is set at the React level
  }

  /**
   * Initialize systems
   * Called before initEventListeners in the manager lifecycle
   */
  async initSystems() {
    // Initialize AppCreator tool + AI-tool + inference systems before event listeners are set up
    this.appCreatorToolSystem = new AppCreatorToolSystem();
    this.appCreatorAIToolSystem = new AppCreatorAIToolSystem();
    this.appCreatorAIInferenceSystem = new AppCreatorAIInferenceSystem();

    // Set up cross-references between the three AppCreator systems
    this.appCreatorToolSystem.setAIToolSystem(this.appCreatorAIToolSystem);
    this.appCreatorToolSystem.setAIInferenceSystem(
      this.appCreatorAIInferenceSystem
    );

    this.appCreatorAIToolSystem.setToolSystem(this.appCreatorToolSystem);
    this.appCreatorAIToolSystem.setAIInferenceSystem(
      this.appCreatorAIInferenceSystem
    );

    this.appCreatorAIInferenceSystem.setToolSystem(this.appCreatorToolSystem);
    this.appCreatorAIInferenceSystem.setAIToolSystem(
      this.appCreatorAIToolSystem
    );
    this.appCreatorAIInferenceSystem.setAppCreatorManager(this);

    // Register tool sets
    const appCreatorToolSet = new AppCreatorToolSet();
    this.appCreatorAIToolSystem.registerToolSet(appCreatorToolSet);

    logger.log(
      {
        tags: "manager|main|systems",
        color1: "lime",
      },
      "AppCreator systems initialized successfully"
    );
  }

  /**
   * Initialize event listeners
   * In React: Use useEffect to listen for events
   */
  async initEventListeners(onAIContentUpdate = null) {
    if (!this.appCreatorAIInferenceSystem) {
      logger.warn(
        {
          tags: "manager|main|listeners",
          color1: "yellow",
        },
        "Cannot initialize AI event listeners - AI system not initialized yet"
      );
      return;
    }

    const aiSystem = this.appCreatorAIInferenceSystem;

    // Set up AI inference event listeners
    const handleBeginSegment = (messageId, segment) => {
      logger.log(
        {
          tags: "manager|main|ai|segment",
          color1: "blue",
        },
        "AI Begin Segment:",
        {
          messageId,
          content:
            segment?.content?.substring(0, 50) +
            (segment?.content?.length > 50 ? "..." : ""),
        }
      );
    };

    // Helper function to parse partial JSON content from AI streaming
    const parsePartialAIContent = (content) => {
      if (!content || typeof content !== "string") return null;

      try {
        // Try to parse complete JSON first
        const parsed = JSON.parse(content);
        return {
          title: parsed.title || "",
          description: parsed.description || "",
          startingMessage: parsed.startingMessage || "",
          isComplete: true,
        };
      } catch (error) {
        // If not complete JSON, try to extract partial data
        try {
          // Look for title field
          const titleMatch = content.match(/"title"\s*:\s*"([^"]*)/);
          const title = titleMatch ? titleMatch[1] : "";

          // Look for description field
          const descMatch = content.match(/"description"\s*:\s*"([^"]*)/);
          const description = descMatch ? descMatch[1] : "";

          // Look for startingMessage field
          const msgMatch = content.match(/"startingMessage"\s*:\s*"([^"]*)/);
          const startingMessage = msgMatch ? msgMatch[1] : "";

          // Only return if we have at least some content
          if (title || description || startingMessage) {
            return {
              title,
              description,
              startingMessage,
              isComplete: false,
            };
          }
        } catch (partialError) {
          // Ignore partial parsing errors
        }
      }

      return null;
    };

    const handleUpdateSegment = (messageId, segmentId, segment) => {
      // Parse the accumulating content for live preview
      const parsedContent = parsePartialAIContent(segment?.content);

      // Update the React component if callback provided (either from init or set later)
      const callback = onAIContentUpdate || this.aiContentUpdateCallback;
      if (callback && parsedContent) {
        callback(parsedContent);
      }

      logger.log(
        {
          tags: "manager|main|ai|segment",
          color1: "cyan",
        },
        "AI Update Segment:",
        {
          messageId,
          segmentId,
          delta: segment?.delta,
          parsedContent: parsedContent
            ? {
                title:
                  parsedContent.title?.substring(0, 30) +
                  (parsedContent.title?.length > 30 ? "..." : ""),
                description:
                  parsedContent.description?.substring(0, 50) +
                  (parsedContent.description?.length > 50 ? "..." : ""),
                startingMessage:
                  parsedContent.startingMessage?.substring(0, 50) +
                  (parsedContent.startingMessage?.length > 50 ? "..." : ""),
                isComplete: parsedContent.isComplete,
              }
            : null,
        }
      );
    };

    const handleEndSegment = (messageId, segmentId, segment) => {
      logger.log(
        {
          tags: "manager|main|ai|segment",
          color1: "green",
        },
        "AI End Segment:",
        {
          messageId,
          segmentId,
          finalContent:
            segment?.content?.substring(0, 100) +
            (segment?.content?.length > 100 ? "..." : ""),
          fullLength: segment?.content?.length,
        }
      );
    };

    const handleComplete = (messageId, result) => {
      logger.log(
        {
          tags: "manager|main|ai|complete",
          color1: "lime",
        },
        "AI Inference Complete:",
        {
          messageId,
          finishReason: result?.finishReason,
          usage: result?.usage,
        }
      );
    };

    const handleError = (messageId, error) => {
      logger.error(
        {
          tags: "manager|main|ai|error",
          color1: "red",
        },
        "AI Inference Error:",
        {
          messageId,
          error: error?.message || error,
        }
      );
    };

    // Add event listeners
    aiSystem.addEventListener("onBeginSegment", handleBeginSegment);
    aiSystem.addEventListener("onUpdateSegment", handleUpdateSegment);
    aiSystem.addEventListener("onEndSegment", handleEndSegment);
    aiSystem.addEventListener("onComplete", handleComplete);
    aiSystem.addEventListener("onError", handleError);

    logger.log(
      {
        tags: "manager|main|listeners",
        color1: "lime",
      },
      "AI event listeners initialized successfully"
    );

    // Store cleanup function for later use
    this.cleanupAIListeners = () => {
      aiSystem.removeEventListener("onBeginSegment", handleBeginSegment);
      aiSystem.removeEventListener("onUpdateSegment", handleUpdateSegment);
      aiSystem.removeEventListener("onEndSegment", handleEndSegment);
      aiSystem.removeEventListener("onComplete", handleComplete);
      aiSystem.removeEventListener("onError", handleError);

      logger.log(
        {
          tags: "manager|main|listeners",
          color1: "gray",
        },
        "AI event listeners cleaned up"
      );
    };

    // Store the callback for later use
    this.aiContentUpdateCallback = onAIContentUpdate;
  }

  /**
   * Initialize states and data
   * In React: Load hero data and initialize AppCreator systems
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|main|init",
        color1: "cyan",
      },
      "Initializing MainSection manager states"
    );

    try {
      // Initialize the AI inference system chat (systems already initialized in initSystems)
      this.appCreatorAIInferenceSystem.initializeChat();

      // Simulate loading hero data or fetching from API
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Could fetch dynamic content here in the future
      // const response = await fetch('/api/hero-content');
      // this.heroData = await response.json();

      this.isInitialized = true;

      logger.log(
        {
          tags: "manager|main|ready",
          color1: "lime",
        },
        "MainSection manager states initialized successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|main|error",
          color1: "red",
          color2: "orange",
          includeSource: true,
        },
        "Failed to initialize MainSection manager states:",
        error
      );

      throw error;
    }
  }

  /**
   * Send message through the appropriate AI system and create app
   * @param {Object} params - Message parameters
   * @param {string} params.userMessage - User's message content
   * @param {Object} params.user - User object for authentication
   */
  async sendMessage({ userMessage, user, template = "basic" }) {
    if (!userMessage || typeof userMessage !== "string") {
      throw new Error("userMessage is required and must be a string");
    }

    if (!this.appCreatorAIInferenceSystem) {
      throw new Error("AppCreator AI Inference System not initialized");
    }

    logger.log(
      {
        tags: "manager|main|send",
        color1: "cyan",
      },
      "Sending message through AppCreator system:",
      userMessage
    );

    // Delegate to AI inference system - it handles its own logging
    const result = await this.appCreatorAIInferenceSystem.processAppIdea(
      userMessage.trim(),
      user?.id
    );

    logger.log(
      {
        tags: "manager|main|send|complete",
        color1: "green",
      },
      "Message sent through AppCreator AI system, result:",
      {
        title: result.title,
        hasDescription: !!result.description,
        hasStartingMessage: !!result.startingMessage,
      }
    );

    // Now create the app and navigate to workspace
    await this.createAppAndNavigate(result, user, template);
  }

  /**
   * Create app from AI result and navigate to workspace
   * @param {object} aiResult - AI-processed result with title, description, startingMessage, originalMessage
   * @param {object} user - User object for authentication
   */
  async createAppAndNavigate(aiResult, user, template = "basic") {
    const payload = {
      name: aiResult.title,
      description: aiResult.description,
      template: template,
      checkpoint: null, // No checkpoint
      ownerId: user?.id || null,
      color: aiResult.color, // AI-generated representative color
      startingMessage: aiResult.startingMessage, // AI-generated detailed instructions
    };

    try {
      logger.log(
        {
          tags: "manager|main|action",
          color1: "cyan",
        },
        "Creating app directly from main section:",
        {
          title: aiResult.title,
          description: aiResult.description,
          template: template,
        }
      );

      const res = await fetch("/api/apps", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        // Parse structured error response
        const errorMessage = data?.error || "Failed to create app";
        const errorCode = data?.errorCode || "VALIDATION_ERROR";
        const details = data?.details || {};
        
        // Log structured error for debugging
        logger.error(
          {
            tags: "manager|main|action|error",
            color1: "red",
          },
          `App creation failed: ${errorCode}`,
          { errorCode, details, errorMessage }
        );
        
        // Throw structured error object so component can parse it
        const error = new Error(errorMessage);
        error.errorCode = errorCode;
        error.error = errorMessage;
        error.details = details;
        throw error;
      }

      const app = data.app;

      logger.log(
        {
          tags: "manager|main|action|success",
          color1: "lime",
        },
        "App created successfully from main section",
        {
          appId: app?._id,
          title: aiResult.title,
          description: aiResult.description,
        }
      );

      // Store AI payload for workspace section using new key-based system
      const aiPayload = {
        title: aiResult.title,
        description: aiResult.description,
        startingMessage: aiResult.startingMessage,
        originalMessage: aiResult.originalMessage,
        color: aiResult.color,
      };

      logger.log(
        {
          tags: "manager|main|action|navigation",
          color1: "yellow",
        },
        "Storing AI payload and navigating to workspace:",
        {
          appId: app._id,
          hasTitle: !!aiPayload.title,
          hasDescription: !!aiPayload.description,
          hasStartingMessage: !!aiPayload.startingMessage,
        }
      );

      // Store payload using new key-based system
      navigationManager.setPayload("workspace-ai-data", aiPayload);

      // Navigate directly to workspace
      await navigationManager.navigateToPath(`/workspace/${app._id}`);
    } catch (error) {
      logger.error(
        {
          tags: "manager|main|action|error",
          color1: "red",
        },
        "Failed to create app from main section:",
        error
      );
      throw error;
    }
  }

  /**
   * Handle "Learn More" button click
   * For now, navigate to app - could scroll to features or show modal
   */
  async handleLearnMore() {
    logger.log(
      {
        tags: "manager|main|action",
        color: "cyan",
      },
      "Learn More button clicked - navigating to app"
    );

    await navigationManager.navigateTo("app");
  }

  /**
   * Get the current initialization status
   * Used by React hook adapter
   */
  getIsInitialized() {
    return this.isInitialized;
  }

  /**
   * Get hero data for the landing page
   */
  getHeroData() {
    return this.heroData;
  }

  /**
   * Set the AI content update callback (for live preview)
   * @param {Function} callback - Function to call with parsed AI content
   */
  setAIContentUpdateCallback(callback) {
    this.aiContentUpdateCallback = callback;
  }

  /**
   * Cleanup method for React useEffect cleanup
   */
  cleanup() {
    // Clean up AI event listeners if they exist
    if (this.cleanupAIListeners) {
      this.cleanupAIListeners();
    }

    // Clean up other resources
    if (this.appCreatorAIInferenceSystem) {
      this.appCreatorAIInferenceSystem.destroy();
    }

    logger.log(
      {
        tags: "manager|main|cleanup",
        color1: "gray",
      },
      "MainManager cleanup completed"
    );
  }
}
